import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { TextInput, Button, ActionIcon, Stack, Text, Group, Radio, Divider, Switch, Box, Select, Flex } from '@mantine/core'
import { IconTrash, IconPlus } from '@tabler/icons-react'
import toast from "react-hot-toast";
import { useMutation } from '@tanstack/react-query';

import { urlValidation } from "@util/validation";
import { getUuid } from "@util/getUuid";

const defaultFormData = {
  url: "",
  url_method: "",
  x_api_key: "",
  data_target: "",
  ml_api_enabled: false,
  header_fields: [],
  name: "",
}


export default function DataStateCallbackLogs({ effortId }) {
  const [logs, setLogs] = useState([]);
  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    fetchLogs()
  }, [])

  function fetchLogs() {
    axios.get(`/efforts/${effortId}/get-data-state-callback-settings/`).then(({ data }) => {
      setLogs(data.response)
    }).catch((() => {
      setLogs([]);
    }))
  }

  const submitMutation = useMutation({
    mutationFn: async () => {
      await axios.post(`/efforts/${effortId}/update-data-state-callback-settings/`, {
        campaign_effort_id: effortId,
        state_callback_settings: [
          ...logs,
          {
            uuid: getUuid(),
            name: formData.name,
            ml_api_enabled: formData.ml_api_enabled,
            data_target: parseInt(formData.data_target),
            url: formData.url,
            method: parseInt(formData.url_method),
            headers: Object.fromEntries(formData.header_fields.map((headerField) => [headerField.name, headerField.value])),
          }
        ],
      });
    },
    onSuccess: () => {
      fetchLogs()
      setFormData(defaultFormData)
    },
    onError: (err) => {
      toast.error(err);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (uuid) => {
      await axios.post(`/efforts/${effortId}/update-data-state-callback-settings/`, {
        campaign_effort_id: effortId,
        state_callback_settings: [
          ...logs,
        ].filter((log) => log.uuid !== uuid),
      });
    },
    onSuccess: () => {
      fetchLogs()
    },
    onError: (err) => {
      toast.error(err);
    },
  });

  const formDisabled = !formData.url || !formData.url_method || !urlValidation.test(formData.url) || !formData.data_target || formData.header_fields.length === 0 || !formData.name;

  return (
    <div>
      <Text fw={600} size="lg">Callback Logs</Text>
      {logs.length > 0 && <>
        <Stack mt="sm">
          {logs.map((log) => (
            <Box key={log.uuid} p="sm" bg="gray.0" style={{ border: "1px solid var(--mantine-color-gray-2)", borderRadius: "var(--mantine-radius-md)" }}>
              <Group align="flex-start">
                <div style={{ flexGrow: 1 }}>
                  <Flex gap="xs" align="center">
                    <Text fw={600}>{log.name || "Unnamed Log"}</Text>
                    {log.ml_api_enabled && <Text size="sm" c="green" fw={600}>ML API</Text>}
                  </Flex>
                  <Text>{log.url}</Text>
                  <Text>{DataStateCallbackLog.URL_METHODS[log.method]}</Text>
                  <Text>{DataStateCallbackLog.DATA_TARGETS[log.data_target]}</Text>
                  <div>
                    {Object.entries(log.headers).map(([key, value]) => (
                      <Text key={key}>{key}: {value}</Text>
                    ))}
                  </div>
                </div>
                <ActionIcon variant="light" radius="xl" color="red" onClick={() => deleteMutation.mutate(log.uuid)} loading={deleteMutation.isPending}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Box>
          ))}
        </Stack>
        <Divider my="lg" />
      </>}
      <div>
        <Stack>
          <TextInput required label="Name" placeholder="Short description of the log" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
          <Switch checked={formData.ml_api_enabled} label="This is a ML API" onChange={(e) => setFormData({ ...formData, ml_api_enabled: e.target.checked })} />
          <TextInput required label="URL" placeholder="URL" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} />
          <Radio.Group required label="URL Method" placeholder="URL Method" value={formData.url_method} onChange={(e) => setFormData({ ...formData, url_method: e })}>
            <Stack mt="xs">
              <Radio value="1" label={DataStateCallbackLog.URL_METHODS[1]} />
              <Radio value="2" label={DataStateCallbackLog.URL_METHODS[2]} />
              <Radio value="3" label={DataStateCallbackLog.URL_METHODS[3]} />
              <Radio value="4" label={DataStateCallbackLog.URL_METHODS[4]} />
            </Stack>
          </Radio.Group>
          <Radio.Group required label="Data Target" placeholder="Data Target" value={formData.data_target} onChange={(e) => setFormData({ ...formData, data_target: e })}>
            <Stack mt="xs">
              {dataTargetOptions.map((option) => (
                <Radio key={option.value} value={option.value} label={option.label} />
              ))}
            </Stack>
          </Radio.Group>
          <div>
            <Text fw={500} size="sm" mb="xs">Header Fields</Text>
            {formData.header_fields.length > 0 && <Stack mb="sm" gap="xs">
              {formData.header_fields.map((headerField) => (
                <HeaderFieldItem key={headerField.uuid} headerField={headerField} onRemove={() => setFormData({ ...formData, header_fields: formData.header_fields.filter((h) => h.uuid !== headerField.uuid) })} />
              ))}
            </Stack>}
            <HeaderFieldCreate setHeaderFields={(e) => setFormData({ ...formData, header_fields: [...formData.header_fields, { ...e, uuid: getUuid() }] })} />
          </div>
          <Button disabled={formDisabled} onClick={() => submitMutation.mutate()} loading={submitMutation.isPending}>Submit</Button>
        </Stack>
      </div>
    </div >
  )
}

const DataStateCallbackLog = {
  URL_METHODS: {
    1: 'POST',
    2: 'GET',
    3: 'PUT',
    4: 'DELETE',
  },
  DATA_TARGETS: {
    1: 'Sweepstakes Registration',
    2: 'Ride Challenge Registration',
    3: 'ML Readings Submission',
    4: "ML Reading Deactivation"
  },
}

const dataTargetOptions = Object.entries(DataStateCallbackLog.DATA_TARGETS).map(([key, value]) => ({ label: value, value: key }));


const HeaderFieldCreate = ({ setHeaderFields }) => {
  const [headerField, setHeaderField] = useState({ name: "", value: "" });
  return (
    <Flex gap="xs" style={{ width: "100%" }} align="flex-end">
      <TextInput style={{ flexGrow: 1 }} label="Key" placeholder="Key" value={headerField.name} onChange={(e) => setHeaderField({ ...headerField, name: e.target.value })} />
      <TextInput style={{ flexGrow: 1 }} label="Value" placeholder="Value" value={headerField.value} onChange={(e) => setHeaderField({ ...headerField, value: e.target.value })} />
      <ActionIcon disabled={!headerField.name || !headerField.value} variant="light" radius="xl" color="green" onClick={() => {
        setHeaderFields(headerField);
        setHeaderField({ name: "", value: "" });
      }}>
        <IconPlus size={16} />
      </ActionIcon>
    </Flex>
  )
}

const HeaderFieldItem = ({ headerField, onRemove }) => {
  return (
    <Box p="sm" bg="yellow.0" style={{ border: "1px solid var(--mantine-color-gray-2)", borderRadius: "var(--mantine-radius-md)" }}>
      <Flex gap="xs" style={{ width: "100%" }} align="flex-end">
        <Flex gap="sm" align="center" style={{ flexGrow: 1 }}>
          <Text fw={600}>{headerField.name}</Text>
          <Text style={{ maxWidth: "500px", overflowWrap: "break-word" }}>{headerField.value}</Text>
        </Flex>
        <ActionIcon variant="light" radius="xl" color="red" onClick={() => onRemove(headerField.uuid)}>
          <IconTrash size={16} />
        </ActionIcon>
      </Flex>
    </Box>
  )
}